import React, { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { fmtNum, parseSection, areaPerMetre, computeArea } from "./sections";

function SectionRow({ r, i, testid, onUpdate, onDelete }) {
    const invalid = !r.section || areaPerMetre(parseSection(r.section)) === 0;
    return (
        <tr className={`border-b border-slate-100 last:border-0 ${r.edited ? "bg-amber-50/40" : ""}`} data-testid={`${testid}-row-${i}`}>
            <td className="py-1.5 pr-2">
                <input
                    type="text"
                    value={r.section}
                    onChange={(e) => onUpdate({ section: e.target.value })}
                    placeholder="es. 500x300 o Ø 250"
                    data-testid={`${testid}-section-${i}`}
                    className={`w-full px-2 py-1 font-mono text-cb-dark bg-transparent border-b ${invalid ? "border-red-300" : "border-transparent hover:border-slate-300 focus:border-cb-accent"} focus:outline-none`}
                />
                {r.note && <div className="px-2 text-[10px] leading-tight text-slate-400" data-testid={`${testid}-note-${i}`}>{r.note}</div>}
            </td>
            <td className="py-1.5 text-center text-slate-400 text-xs hidden sm:table-cell">{r.tracks ?? "—"}</td>
            <td className="py-1.5 pl-2 pr-1">
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={r.length_m ?? 0}
                    onChange={(e) => onUpdate({ length_m: Number(e.target.value) })}
                    data-testid={`${testid}-length-${i}`}
                    className="w-full text-right px-2 py-1 text-cb-dark bg-transparent border-b border-transparent hover:border-slate-300 focus:border-cb-accent focus:outline-none"
                />
            </td>
            <td className="py-1.5 text-right font-semibold text-cb-dark tabular-nums pr-2">{fmtNum(r.area_m2 || 0)}</td>
            <td className="py-1.5 text-right">
                <button onClick={onDelete} title="Rimuovi riga" data-testid={`${testid}-del-${i}`}
                    className="text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 size={14} />
                </button>
            </td>
        </tr>
    );
}

export function EditableResultsTable({ title, rows, onChange, testid }) {
    const totalArea = useMemo(() => rows.reduce((s, r) => s + (Number(r.area_m2) || 0), 0), [rows]);
    const totalLength = useMemo(() => rows.reduce((s, r) => s + (Number(r.length_m) || 0), 0), [rows]);

    const updateRow = (key, patch) => onChange(rows.map((r) => {
        if (r._key !== key) return r;
        const merged = { ...r, ...patch, edited: true };
        merged.area_m2 = computeArea(merged.section, merged.length_m);
        return merged;
    }));
    const addRow = () => onChange([...rows, { _key: `new-${Date.now()}`, section: "", length_m: 0, tracks: 0, area_m2: 0, edited: true, added: true }]);
    const deleteRow = (key) => onChange(rows.filter((r) => r._key !== key));

    return (
        <div data-testid={testid} className="bg-white border border-slate-200 p-6">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display text-xl font-bold text-cb-dark">{title}</h3>
                <div className="text-right">
                    <div className="text-cb-accent font-display text-2xl font-bold" data-testid={`${testid}-total-area`}>{fmtNum(totalArea)} m²</div>
                    <div className="text-xs text-slate-500">lamiera · <span data-testid={`${testid}-total-length`}>{fmtNum(totalLength)}</span> m lineari</div>
                </div>
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
                        <th className="text-left py-2 font-medium">Sezione</th>
                        <th className="text-center py-2 font-medium hidden sm:table-cell">Tratti</th>
                        <th className="text-right py-2 font-medium">Metri lineari</th>
                        <th className="text-right py-2 font-medium">m² lamiera</th>
                        <th className="w-8" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <SectionRow key={r._key} r={r} i={i} testid={testid}
                            onUpdate={(patch) => updateRow(r._key, patch)} onDelete={() => deleteRow(r._key)} />
                    ))}
                </tbody>
            </table>
            <button onClick={addRow} data-testid={`${testid}-add`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-cb-accent hover:text-cb-dark transition-colors">
                <Plus size={14} /> Aggiungi sezione
            </button>
        </div>
    );
}
